import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import StockItemCard from '../components/StockItemCard'
import { usePermissions } from '../contexts/PermissionContext'
import ConfirmationModal from '../components/ConfirmationModal'
import Select from 'react-select'
import '../styles/selectControl.css'
import { vendorAPI } from '../api/vendorAPI'

function InventoryAmendScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const { inventoryItemId } = useParams()
    const [searchParams] = useSearchParams()
    const [error, setError] = useState(null)
    const [warehouses, setWarehouses] = useState([])
    const [locations, setLocations] = useState([])
    const [vendorMap, setVendorMap] = useState({})
    const [inventoryItem, setInventoryItem] = useState({
        stock_item: '',
        stockItemData: null,
        warehouse: { _id: '' },
        location: '',
        quantity: '',
        lot: '',
        notes: '',
        bb_date: '',
        comments: '',
        vendor: '',
        cost: '',
    })
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    useEffect(() => {
        let isMounted = true;

        async function fetchInventoryItem() {
            try {
                const item = await inventoryItemAPI.getById(inventoryItemId)
                if (!isMounted) return;
                
                if (!item) return;

                const matchingWarehouse = warehouses.find(w => w.name === item.warehouse_name)
                
                if (matchingWarehouse) {
                    const warehouseLocations = await locationAPI.getAll(matchingWarehouse._id)
                    if (!isMounted) return;

                    setLocations(warehouseLocations)
                    
                    const matchingLocation = warehouseLocations.find(l => l.name === item.location_name)
                    
                    const formattedItem = {
                        stock_item: item.stock_item._id,
                        stockItemData: item.stock_item,
                        warehouse: matchingWarehouse,
                        location: matchingLocation?._id || '',
                        quantity: item.quantity || '',
                        lot: item.lot || '',
                        notes: item.notes || '',
                        bb_date: item.bb_date || '',
                        comments: item.comments || '',
                        vendor: item.vendor || '',
                        cost: item.cost || '',
                    }
                    
                    setInventoryItem(formattedItem)
                }
            } catch (error) {
                if (!isMounted) return;
                setError('Failed to load inventory item')
            }
        }

        if (inventoryItemId && warehouses.length > 0) {
            fetchInventoryItem()
        }

        return () => {
            isMounted = false;
        }
    }, [inventoryItemId, warehouses])

    useEffect(() => {
        let isMounted = true;

        async function fetchWarehouses() {
            try {
                const response = await warehouseAPI.getAll()
                if (!isMounted) return;
                setWarehouses(response)
            } catch (error) {
                if (!isMounted) return;
                setError('Failed to load warehouses')
            }
        }
        
        fetchWarehouses()

        return () => {
            isMounted = false;
        }
    }, [])

    useEffect(() => {
        let isMounted = true;

        async function fetchLocations() {
            try {
                if (!inventoryItem.warehouse._id) {
                    setLocations([])  // Clear locations if no warehouse selected
                    return;
                }
                const response = await locationAPI.getAll(inventoryItem.warehouse._id)
                if (!isMounted) return;
                setLocations(response)
            } catch (error) {
                if (!isMounted) return;
                setError('Failed to load locations')
            }
        }
        
        fetchLocations()

        return () => {
            isMounted = false;
        }
    }, [inventoryItem.warehouse._id])

    useEffect(() => {
        let isMounted = true;

        async function fetchVendors() {
            try {
                const vendors = await vendorAPI.getAll()
                if (!isMounted) return;
                
                const mapping = vendors.reduce((acc, vendor) => {
                    acc[vendor._id] = vendor.name
                    return acc
                }, {})
                setVendorMap(mapping)
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching vendors:', error)
            }
        }
        
        fetchVendors()

        return () => {
            isMounted = false;
        }
    }, [])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            const updateData = {
                stock_item_id: inventoryItem.stock_item,
                warehouse: inventoryItem.warehouse._id,
                location: inventoryItem.location,
                quantity: inventoryItem.quantity,
                lot: inventoryItem.lot,
                notes: inventoryItem.notes,
                bb_date: inventoryItem.bb_date,
                comments: inventoryItem.comments,
                vendor: inventoryItem.vendor,
                cost: inventoryItem.cost,
            }
            await inventoryItemAPI.update(inventoryItemId, updateData)
            const returnTo = searchParams.get('returnTo')
            const targetUrl = returnTo || '/inventory'
            
            // Add refresh parameter if navigating to inventory
            if (targetUrl.includes('/inventory')) {
                const separator = targetUrl.includes('?') ? '&' : '?'
                const refreshParam = `${separator}refresh=1`
                navigate(`${targetUrl}${refreshParam}`)
            } else {
                navigate(targetUrl)
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleDelete = async () => {
        try {
            await inventoryItemAPI.delete(inventoryItemId, {
                notes: inventoryItem.notes
            })
            const returnTo = searchParams.get('returnTo')
            const targetUrl = returnTo || '/inventory'
            
            // Add refresh parameter if navigating to inventory
            if (targetUrl.includes('/inventory')) {
                const separator = targetUrl.includes('?') ? '&' : '?'
                const refreshParam = `${separator}refresh=1`
                navigate(`${targetUrl}${refreshParam}`)
            } else {
                navigate(targetUrl)
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('inventory.receive')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to edit inventory items.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
            <Col md={8} lg={6}>
            <h1>Edit Inventory Item {inventoryItemId}</h1>
            {inventoryItem.stockItemData && (
                <StockItemCard stockItem={inventoryItem.stockItemData}/>
            )}
            <div style={{ height: '20px' }}></div>
            <Form onSubmit={submitHandler}>
                <Form.Group className="mb-4" controlId="warehouse">
                    <Form.Label>Warehouse*</Form.Label>
                    <Select
                        value={warehouses.find(w => w._id === inventoryItem.warehouse._id) ? {
                            value: inventoryItem.warehouse._id,
                            label: warehouses.find(w => w._id === inventoryItem.warehouse._id)?.name || 'Select Warehouse'
                        } : null}
                        onChange={(selectedOption) => setInventoryItem({
                            ...inventoryItem,
                            warehouse: { _id: selectedOption ? selectedOption.value : '' },
                            location: ''  // Clear location when warehouse changes
                        })}
                        options={warehouses.map(warehouse => ({
                            value: warehouse._id,
                            label: warehouse.name
                        }))}
                        isSearchable
                        required
                        placeholder="Select Warehouse"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="location">
                    <Form.Label>Location*</Form.Label>
                    <Select
                        value={locations.find(l => l._id === inventoryItem.location) ? {
                            value: inventoryItem.location,
                            label: locations.find(l => l._id === inventoryItem.location)?.name || 'Select Location'
                        } : null}
                        onChange={(selectedOption) => setInventoryItem({
                            ...inventoryItem, 
                            location: selectedOption ? selectedOption.value : ''
                        })}
                        options={locations.map(location => ({
                            value: location._id,
                            label: location.name
                        }))}
                        isSearchable
                        required
                        placeholder="Select Location"
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isDisabled={!inventoryItem.warehouse._id}
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="vendor">
                    <Form.Label>Vendor*</Form.Label>
                    <Select
                        value={inventoryItem.vendor ? { 
                            value: inventoryItem.vendor, 
                            label: vendorMap[inventoryItem.vendor] || ''
                        } : null}
                        onChange={(selectedOption) => setInventoryItem({
                            ...inventoryItem,
                            vendor: selectedOption ? selectedOption.value : ''
                        })}
                        options={Object.entries(vendorMap).map(([id, name]) => ({
                            value: id,
                            label: name
                        }))}
                        getOptionValue={(option) => option.value}
                        getOptionLabel={(option) => option.label}
                        isSearchable
                        required
                        placeholder="Select Vendor"
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="cost">
                    <Form.Label>Cost*</Form.Label>
                    <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={inventoryItem.cost}
                        onChange={(e) => setInventoryItem({...inventoryItem, cost: e.target.value})}
                        required
                        placeholder="Enter cost"
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="lot">
                    <Form.Label>LOT</Form.Label>
                    <Form.Control
                        type="text"
                        value={inventoryItem.lot}
                        onChange={(e) => setInventoryItem({...inventoryItem, lot: e.target.value})}
                        placeholder="Enter LOT"
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="notes">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        type="text"
                        value={inventoryItem.notes}
                        onChange={(e) => setInventoryItem({...inventoryItem, notes: e.target.value})}
                        placeholder="Enter notes"
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="bb_date">
                    <Form.Label>Best Before Date</Form.Label>
                    <Form.Control
                        type="date"
                        value={inventoryItem.bb_date || ''}
                        onChange={(e) => setInventoryItem({...inventoryItem, bb_date: e.target.value})}
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="quantity">
                    <Form.Label>Quantity*</Form.Label>
                    <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        value={inventoryItem.quantity}
                        onChange={(e) => setInventoryItem({...inventoryItem, quantity: e.target.value})}
                        required
                        placeholder="Enter quantity"
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="comments">
                    <Form.Label>Comments</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        value={inventoryItem.comments}
                        onChange={(e) => setInventoryItem({...inventoryItem, comments: e.target.value})}
                        placeholder="Enter ammendment comments (max 500 characters)"
                        autoComplete="off"
                        spellCheck="true"
                    />
                    <Form.Text className="text-muted">
                        {inventoryItem.comments?.length || 0}/500 characters
                    </Form.Text>
                </Form.Group>
                {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
                )}
                <Button variant="success" type="submit" className="me-2">
                    Save Changes
                </Button>
                
                <Button 
                    variant="danger" 
                    onClick={() => setShowDeleteModal(true)}
                    className="me-2"
                >
                    Delete
                </Button>

                <Button 
                    variant="secondary" 
                    onClick={() => navigate(-1)}
                >
                    Cancel
                </Button>
            </Form>

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Inventory Item"
                message="Are you sure you want to delete this inventory item?"
            />
            </Col>
            </Row>
        </Container>
    )
}

export default InventoryAmendScreen 